import type { PriceTable } from "../pricing/pricing";
import type { Phase, TurnEvent } from "../types";
import type { ClassifiedTurn } from "./classify";

export function isPremiumModel(model: string): boolean {
  return (
    model.startsWith("claude-opus") ||
    model.startsWith("claude-fable") ||
    model.startsWith("claude-mythos")
  );
}

/** Cheapest model that comfortably handles each phase. */
export const IDEAL_MODEL_BY_PHASE: Partial<Record<Phase, string>> = {
  exploration: "claude-haiku-4-5",
  execution: "claude-sonnet-4-5",
};

/** Waste attribution groups a session together with all its subagent runs. */
export function rootSessionId(turn: TurnEvent): string {
  return turn.parentSessionId ?? turn.sessionId;
}

export interface PhaseModelCell {
  turns: number;
  costUsd: number;
  wasteUsd: number;
  tokens: number;
}

export interface SessionAggregate {
  sessionId: string;
  project: string;
  slug?: string;
  firstTs: number;
  lastTs: number;
  turns: number;
  costUsd: number;
  wasteUsd: number;
  wasteExplorationUsd: number;
  wasteExecutionUsd: number;
  premiumTurns: number;
  models: Set<string>;
}

export interface WasteReport {
  totalCostUsd: number;
  totalWasteUsd: number;
  wasteByPhase: { exploration: number; execution: number };
  fastModeExtraUsd: number;
  unpricedTurns: number;
  unpricedModels: string[];
  /** model -> phase -> aggregate (uses smoothed phases). */
  matrix: Map<string, Map<Phase, PhaseModelCell>>;
  perSession: Map<string, SessionAggregate>;
  perProject: Map<string, { costUsd: number; wasteUsd: number; turns: number }>;
}

/**
 * Core waste metric: for premium-model turns spent in exploration/execution,
 * the delta between what the turn cost and what the same token counts would
 * have cost on the phase-appropriate model. Approximation (stated in the
 * report): the cheaper model is assumed to produce the same token counts.
 */
export function computeWaste(classified: ClassifiedTurn[], prices: PriceTable): WasteReport {
  const report: WasteReport = {
    totalCostUsd: 0,
    totalWasteUsd: 0,
    wasteByPhase: { exploration: 0, execution: 0 },
    fastModeExtraUsd: 0,
    unpricedTurns: 0,
    unpricedModels: [],
    matrix: new Map(),
    perSession: new Map(),
    perProject: new Map(),
  };
  const unpricedModels = new Set<string>();

  for (const { turn, phaseSmoothed } of classified) {
    const cost = prices.turnCostUsd(turn);
    if (cost === null) {
      report.unpricedTurns++;
      unpricedModels.add(turn.model);
      continue;
    }
    report.totalCostUsd += cost;

    if (turn.speed === "fast") {
      const standard = prices.costAtModel(turn.model, turn);
      if (standard !== null) report.fastModeExtraUsd += Math.max(0, cost - standard);
    }

    let wasteUsd = 0;
    const idealModel = IDEAL_MODEL_BY_PHASE[phaseSmoothed];
    if (idealModel && isPremiumModel(turn.model)) {
      const idealCost = prices.costAtModel(idealModel, turn);
      if (idealCost !== null) {
        wasteUsd = Math.max(0, cost - idealCost);
        report.totalWasteUsd += wasteUsd;
        if (phaseSmoothed === "exploration") report.wasteByPhase.exploration += wasteUsd;
        if (phaseSmoothed === "execution") report.wasteByPhase.execution += wasteUsd;
      }
    }

    // model × phase matrix
    let phaseMap = report.matrix.get(turn.model);
    if (!phaseMap) {
      phaseMap = new Map();
      report.matrix.set(turn.model, phaseMap);
    }
    let cell = phaseMap.get(phaseSmoothed);
    if (!cell) {
      cell = { turns: 0, costUsd: 0, wasteUsd: 0, tokens: 0 };
      phaseMap.set(phaseSmoothed, cell);
    }
    cell.turns++;
    cell.costUsd += cost;
    cell.wasteUsd += wasteUsd;
    cell.tokens +=
      turn.usage.input + turn.usage.output + turn.usage.cacheRead + turn.usage.cacheWrite;

    // per root session (subagent cost rolls up into the spawning session)
    const sid = rootSessionId(turn);
    let sess = report.perSession.get(sid);
    if (!sess) {
      sess = {
        sessionId: sid,
        project: "",
        slug: undefined,
        firstTs: Infinity,
        lastTs: 0,
        turns: 0,
        costUsd: 0,
        wasteUsd: 0,
        wasteExplorationUsd: 0,
        wasteExecutionUsd: 0,
        premiumTurns: 0,
        models: new Set(),
      };
      report.perSession.set(sid, sess);
    }
    sess.turns++;
    sess.costUsd += cost;
    sess.wasteUsd += wasteUsd;
    if (phaseSmoothed === "exploration") sess.wasteExplorationUsd += wasteUsd;
    if (phaseSmoothed === "execution") sess.wasteExecutionUsd += wasteUsd;
    if (isPremiumModel(turn.model)) sess.premiumTurns++;
    sess.models.add(turn.model);
    if (turn.timestamp > 0) {
      sess.firstTs = Math.min(sess.firstTs, turn.timestamp);
      sess.lastTs = Math.max(sess.lastTs, turn.timestamp);
    }
    if (!sess.project && turn.projectPath) sess.project = turn.projectPath;
    if (!sess.slug && !turn.isSidechain && turn.slug) sess.slug = turn.slug;

    // per project
    const project = turn.projectPath || "(unknown)";
    let proj = report.perProject.get(project);
    if (!proj) {
      proj = { costUsd: 0, wasteUsd: 0, turns: 0 };
      report.perProject.set(project, proj);
    }
    proj.costUsd += cost;
    proj.wasteUsd += wasteUsd;
    proj.turns++;
  }

  report.unpricedModels = [...unpricedModels].sort();
  return report;
}
